import React from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { Users, Home, Phone } from "lucide-react";
import { Link } from "react-router-dom";

// Animation constants matching home page
const bounceTransition = {
	duration: 0.4,
	ease: [0.68, -0.25, 0.265, 1.25],
};

const Section = ({ children, className = "" }) => (
	<section className={`py-12 md:py-24 ${className}`}>
		<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
	</section>
);

const ImageSection = ({
	image,
	title,
	children,
	reverse = false,
	className = "",
}) => (
	<div
		className={`flex flex-col ${
			reverse ? "md:flex-row-reverse" : "md:flex-row"
		} gap-8 items-center ${className}`}
	>
		<motion.div
			initial={{ opacity: 0, x: reverse ? 50 : -50 }}
			whileInView={{ opacity: 1, x: 0 }}
			viewport={{ once: true }}
			transition={{ duration: 0.5 }}
			className="w-full md:w-1/2"
		>
			<img
				src={image}
				alt={title}
				className="w-full h-[400px] object-cover rounded-2xl shadow-lg"
			/>
		</motion.div>
		<motion.div
			initial={{ opacity: 0, x: reverse ? -50 : 50 }}
			whileInView={{ opacity: 1, x: 0 }}
			viewport={{ once: true }}
			transition={{ duration: 0.5 }}
			className="w-full md:w-1/2 space-y-6"
		>
			{title && <h2 className="text-3xl font-bold text-gray-900">{title}</h2>}
			<div className="text-lg text-gray-600 space-y-4">{children}</div>
		</motion.div>
	</div>
);

const ContactBanner = () => (
	<div className="relative h-[400px] rounded-2xl overflow-hidden">
		<img
			src="/images/about/showroom.jpg"
			alt="Our showroom"
			className="absolute inset-0 w-full h-full object-cover"
		/>
		<div className="absolute inset-0 bg-black/60">
			<div className="h-full flex flex-col items-center justify-center text-white">
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					className="text-center"
				>
					<h2 className="text-4xl font-bold mb-4">Get In Touch</h2>
					<p className="text-lg text-gray-200 mb-8">
						Have questions? We're here to help.
					</p>
					<Link
						to="/contact"
						className="inline-flex items-center px-6 py-3 bg-white text-gray-900 rounded-lg font-medium hover:bg-gray-100 transition-colors"
					>
						<Phone className="w-5 h-5 mr-2" />
						Contact Us
					</Link>
				</motion.div>
			</div>
		</div>
	</div>
);

export default function AboutUsScreen() {
	return (
		<>
			<Helmet>
				<link rel="canonical" href="https://offcutshades.com/about" />
				<title>
					About Us: Get to Know Your Window Shade Experts | Off Cut Shades
				</title>
				<meta
					name="description"
					content="Get to know your window shade experts local in Canada. Our team here at Off Cut Shades has years of experience in custom shades and blinds."
				/>
				<script type="application/ld+json">
					{`{
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://offcutshades.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "About Us",
                "item": "https://offcutshades.com/about"
              }
            ]
          }`}
				</script>
			</Helmet>

			<main className="bg-gray-50">
				{/* Hero Section */}
				<Section className="bg-white">
					<motion.div
						className="relative text-center max-w-3xl mx-auto mb-16 z-10"
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={bounceTransition}
					>
						<motion.h1
							className="text-5xl md:text-7xl font-bold mb-6 leading-tight"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ ...bounceTransition, delay: 0.2 }}
						>
							About{" "}
							<span className="relative">
								<span className="text-primary">Us</span>
							</span>
						</motion.h1>
						<motion.p
							className="text-xl text-gray-600"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ ...bounceTransition, delay: 0.3 }}
						>
							Simplifying your window treatment shopping experience with
							expertise and innovation.{" "}
						</motion.p>
					</motion.div>
				</Section>

				{/* Who We Are Section */}
				<Section>
					<ImageSection image="/images/about/about-us.jpg" title="Who We Are">
						<p>
							At Off Cut Shades, our goal and commitment is to provide a simple
							alternative to a complicated shopping experience. Our site allows
							you to shop two ways:
						</p>
						<div className="pl-4 border-l-4 border-primary/20 space-y-4">
							<p>
								1. Our in-house design team expertly configured various shade
								options so you don't have to. We've removed the challenges of
								customizing window treatments online. We manage every detail to
								provide curated solutions with fast and reliable lead times. All
								you have to do is pick your size and fabric colour. Or, you can
								use the designed option as a starting point and customize even
								further.
							</p>
							<p>
								2. Use the complete customization options starting by selecting
								your shade type and selection every design detail.
							</p>
						</div>
						<p className="font-medium text-primary">
							Either way your shade will be custom made and shipped within 2
							days.
						</p>
					</ImageSection>
				</Section>

				{/* Local Section */}
				<Section className="bg-white">
					<ImageSection
						image="/images/about/local.jpg"
						title="Supporting Local Communities"
						reverse
					>
						<p>
							By keeping attractive manufacturing jobs here at home, we empower
							our communities to grow and prosper. It benefits everyone to buy
							locally assembled products.
						</p>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
							<div className="flex items-start gap-4">
								<div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
									<Users className="w-6 h-6 text-primary" />
								</div>
								<div>
									<h3 className="font-medium mb-2">Local Expertise</h3>
									<p className="text-gray-600">
										Expert craftsmanship from your community
									</p>
								</div>
							</div>
							<div className="flex items-start gap-4">
								<div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
									<Home className="w-6 h-6 text-primary" />
								</div>
								<div>
									<h3 className="font-medium mb-2">Community Impact</h3>
									<p className="text-gray-600">
										Supporting local economic growth
									</p>
								</div>
							</div>
						</div>
					</ImageSection>
				</Section>

				{/* Contact Banner */}
				<Section>
					<ContactBanner />
				</Section>
			</main>
		</>
	);
}
